import * as React from "react";
import { Input as BaseInput, Box, styled } from "@mui/material";
import { useEffect } from "react";

const grey = {
    50: `#F3F6F9`,
    100: `#E5EAF2`,
    200: `#DAE2ED`,
    300: `#C7D0DD`,
    400: `#B0B8C4`,
    500: `#9DA8B7`,
    600: `#6B7A90`,
    700: `#434D5B`,
    800: `#303740`,
    900: `#1C2025`,
};

const InputElement = styled(`input`)(
    `
  width: 80px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 30px;
  font-weight: 700;
  text-decoration: none;
  line-height: 2;
  padding: 8px 0;
  border-radius: 12px;
  text-align: center;
  border: 1px solid ${grey[200]};
  border-bottom: none;
  background: #F0F0F0;
  color: transparent;
  text-shadow: 0 0 0 ${grey[900]};

  &:focus {
     &:focus {
    border-color: rgb(0,114,56);
    box-shadow: 0 0 0 3px rgb(0,166,81);
  }
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }
`
);

interface OTPProps {
    separator?: React.ReactNode;
    length?: number;
    value?: string;
    onChange?: (updater: string | ((prev: string) => string)) => void;
    onEnter?: () => void;
}

const OTP = ({
    separator = `-`,
    length = 4,
    value = ``,
    onChange = () => {},
    onEnter = () => {},
}: OTPProps): React.ReactElement => {
    const unqid = Array.from(
        { length: 8 },
        () => `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz`[Math.floor(Math.random() * 52)]
    ).join(``);
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>(new Array(length).fill(null));

    const focusInput = (targetIndex: number): void => {
        const targetInput = document.getElementById(`otpinput${unqid}${targetIndex}`);
        if (targetInput) {
            targetInput.focus();
        }
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
        currentIndex: number
    ): void => {
        switch (event.key) {
            case `ArrowUp`:
            case `ArrowDown`:
            case ` `:
                event.preventDefault();
                break;
            case `Enter`:
                onEnter();
                break;
            case `ArrowLeft`:
                event.preventDefault();
                if (currentIndex > 0) {
                    focusInput(currentIndex - 1);
                }
                break;
            case `ArrowRight`:
                event.preventDefault();
                if (currentIndex < length - 1) {
                    focusInput(currentIndex + 1);
                }
                break;
            case `Delete`:
                event.preventDefault();
                onChange(prevOtp => {
                    const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
                    return otp;
                });

                break;
            case `Backspace`:
                event.preventDefault();
                if (currentIndex > 0) {
                    focusInput(currentIndex - 1);
                }

                onChange(prevOtp => {
                    const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
                    return otp;
                });
                break;

            default:
                break;
        }
    };

    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        currentIndex: number
    ): void => {
        const currentValue = event.target.value;

        onChange(prev => {
            const otpArray = Array(4)
                .fill(undefined)
                .map((_x: undefined, i: number) => prev[i] ?? ` `);
            otpArray[currentIndex] = currentValue.replace(prev[currentIndex], ``).toUpperCase();
            return otpArray.join(``);
        });
        if (currentValue !== ``) {
            if (currentIndex < length - 1) {
                focusInput(currentIndex + 1);
            }
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLDivElement>, currentIndex: number): void => {
        focusInput(currentIndex);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>, currentIndex: number): void => {
        event.preventDefault();
        const { clipboardData } = event;

        // Check if there is text data in the clipboard
        if (clipboardData.types.includes(`text/plain`)) {
            let pastedText = clipboardData.getData(`text/plain`);
            pastedText = pastedText.substring(0, length).trim();
            let indexToEnter = 0;

            while (indexToEnter <= currentIndex) {
                if (inputRefs.current[indexToEnter]?.value && indexToEnter < currentIndex) {
                    indexToEnter += 1;
                } else {
                    break;
                }
            }

            const otpArray = value.split(``);

            for (let i = indexToEnter; i < length; i += 1) {
                const lastValue = pastedText[i - indexToEnter] ?? ` `;
                otpArray[i] = lastValue;
            }

            onChange(otpArray.join(``));
        }
    };

    useEffect(() => {
        setTimeout(() => {
            focusInput(0);
        }, 100);
    }, []);

    return (
        <Box sx={{ display: `flex`, gap: 2.5, alignItems: `center` }}>
            {new Array(length).fill(null).map((_, index) => (
                <React.Fragment key={index}>
                    <BaseInput
                        autoComplete="new-password"
                        id={`otpinput${unqid}${index}`}
                        sx={{
                            "&.MuiInputBase-root::before": {
                                borderBottom: `none !important`,
                            },
                            "&.MuiInputBase-root::after": {
                                borderBottom: `none !important`,
                            },
                        }}
                        ref={(ele: HTMLInputElement | null) => {
                            inputRefs.current[index] = ele;
                        }}
                        slots={{
                            input: InputElement,
                        }}
                        aria-label={`Digit ${index + 1} of CODE`}
                        onKeyDown={event => handleKeyDown(event, index)}
                        onChange={event => handleChange(event, index)}
                        onClick={event => handleClick(event, index)}
                        onPaste={event => handlePaste(event, index)}
                        value={value[index] ?? ``}
                    />
                    {index === length - 1 ? null : separator}
                </React.Fragment>
            ))}
        </Box>
    );
};

export default OTP;
